import style from "./Grant.module.scss";
import { Button, proxyNumber } from "@ui";
import { useEffect, useId, useMemo, useState } from "react";
import cn from "classnames";
import { extractError } from "UI/Button/Button";
import { IUser } from "@interfaces/admin/user";
import { useGrantProxyMutation } from "@RTKQuery/admin/customers/customers";
import { useGetAllProxiesQuery } from "@RTKQuery/admin/proxy/proxy";

// Строки — те же, что кнопками в панели. Произвольное число дней не даём:
// опечатка «300» вместо «30» дарит клиенту десять месяцев.
const TERMS = [3, 7, 15, 30];

const OPERATORS = [
    { value: "", label: "Всі оператори" },
    { value: "Kyivstar", label: "Kyivstar" },
    { value: "Vodafone", label: "Vodafone" },
    { value: "lifecell", label: "lifecell" },
];

export function Grant({
    userData
}: {
    userData?: IUser,
}) {
    const [grantProxy, { isSuccess, isLoading, reset, error, data }] = useGrantProxyMutation();

    const fieldId = useId();
    const [operator, setOperator] = useState("");
    const [routerId, setRouterId] = useState("");
    const [days, setDays] = useState<number>(30);
    // ПО УМОЛЧАНИЮ СО СПИСАНИЕМ: выдача — это продажа, а не подарок.
    // Бесплатно только осознанно, и в истории это будет видно отдельно.
    const [free, setFree] = useState(false);

    // Показываем только те, что в продаже: остальные либо служебные, либо
    // выведены владельцем намеренно.
    const { currentData: proxiesData, isFetching, isError: lookupFailed, refetch } = useGetAllProxiesQuery({
        page: 1, limit: 200, router_id: "", phone_number: "",
        operator, selling: "true"
    });

    const modems = useMemo(() => {
        // Запрос отдаёт parent_proxies (родительские модемы), а не proxies.
        // ТОЛЬКО ONLINE: выдать клиенту модем, который сейчас не в сети, значит
        // продать неработающий прокси — он придёт с претензией в тот же день.
        return (proxiesData?.parent_proxies || [])
            .filter((p: any) => p.status === "Online")
            .map((p: any) => ({
                value: String(p.router_id),
                // Короткий номер: клиент знает модем как «№2», а не 1000000002
                label: `№${proxyNumber(p)} · ${p.operator}`
                    + (p.phone_number ? ` · ${p.phone_number}` : "")
            }));
    }, [proxiesData]);

    useEffect(() => {
        // Сменили оператора — прежний выбор мог исчезнуть из списка
        setRouterId("");
    }, [operator]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isSuccess) {
            setRouterId("");
            timer = setTimeout(() => reset(), 3500);
        }
        return () => clearTimeout(timer);
    }, [isSuccess, reset]);

    return (
        <div className={style.container}>
            <p className="text-center text-[#1B1B1D] text-[16px] font-semibold leading-normal mb-[14px]">
                Видати модем клієнту
            </p>

            <label htmlFor={`${fieldId}-operator`} className={style.label}>Оператор:</label>
            <select id={`${fieldId}-operator`} className={style.select} value={operator}
                disabled={isLoading} onChange={event => { setOperator(event.target.value); setRouterId(""); }}>
                {OPERATORS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>

            <label htmlFor={`${fieldId}-modem`} className={style.label}>Модем:</label>
            <select id={`${fieldId}-modem`} className={style.select} value={routerId}
                disabled={isLoading || isFetching || lookupFailed || !modems.length}
                onChange={event => setRouterId(event.target.value)}>
                <option value="">{isFetching ? "Завантаження…" : modems.length ? "Оберіть модем" : "Немає модемів онлайн"}</option>
                {modems.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            {lookupFailed && <p role="alert" className={style.error}>
                Не вдалося завантажити модеми. <button type="button" onClick={() => refetch()}>Спробувати ще раз</button>
            </p>}

            <label className={style.label}>Термін:</label>
            <div className={style.terms}>
                {TERMS.map((d) => (
                    <button
                        key={d}
                        type="button"
                        disabled={isLoading}
                        className={cn(style.term, days === d && style.term_active)}
                        onClick={() => setDays(d)}
                    >
                        {d} дн.
                    </button>
                ))}
            </div>

            <label className={style.free}>
                <input type="checkbox" checked={free} disabled={isLoading}
                    onChange={event => setFree(event.target.checked)} />
                <span>Безкоштовно (без списання з балансу)</span>
            </label>
            <p className={style.hint}>
                {free
                    ? "Видача без оплати — в історії буде позначена окремо."
                    : "З балансу клієнта буде списана вартість тарифу."}
            </p>

            {/* Нода узнаёт об аренде сразу. Не дошло — она догонит сверкой, но
                до этого порт для неё «вільний», и владелец должен это знать. */}
            {isSuccess && data && !data.node_notified && (
                <p className={style.warn}>
                    Видано, але нода не відповіла — порт поки вважається вільним.
                    Вона підхопить на наступній звірці.
                </p>
            )}

            {error && <p role="alert" className={style.error}>{extractError(error)}</p>}
            <Button
                text={isSuccess ? "Успішно видано" : "Видати"}
                color="blue"
                customStyle={{
                    button: "!w-[180px] !h-[44px]",
                    width: "!mx-auto !mt-[8px]"
                }}
                loading={isLoading}
                disabled={!routerId || !userData?.id || isFetching || lookupFailed || !modems.some(item => item.value === routerId)}
                onClick={() => {
                    if (isLoading || !routerId || !userData?.id || isFetching || lookupFailed) return;
                    grantProxy({
                        user_id: userData.id,
                        router_id: Number(routerId),
                        days,
                        charge: !free
                    });
                }}
                resetStatus={() => reset()}
            />
        </div>
    )
}
